/** Query flag appended to customer email links to open the login modal on landing. */
export const EMAIL_SIGNIN_QUERY = 'signin';

export function isEmailSignInPrompt(searchParams: URLSearchParams): boolean {
  const value = searchParams.get(EMAIL_SIGNIN_QUERY);
  return value === '1' || value === 'true' || value === 'yes';
}

export function stripEmailSignInPrompt(searchParams: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(searchParams);
  next.delete(EMAIL_SIGNIN_QUERY);
  return next;
}

/** Per-path capture survives React StrictMode remount after ?signin=1 is stripped from the URL. */
const capturedEmailSignInByPath = new Map<string, boolean>();

/**
 * True if this navigation requested email sign-in (?signin=1).
 * First read wins for the pathname until {@link clearCapturedEmailSignInPrompt} is called.
 */
export function captureEmailSignInPrompt(pathname: string = typeof window !== 'undefined' ? window.location.pathname : ''): boolean {
  if (capturedEmailSignInByPath.has(pathname)) {
    return capturedEmailSignInByPath.get(pathname)!;
  }
  const requested =
    typeof window === 'undefined'
      ? false
      : isEmailSignInPrompt(new URLSearchParams(window.location.search));
  capturedEmailSignInByPath.set(pathname, requested);
  return requested;
}

export function clearCapturedEmailSignInPrompt(pathname: string = typeof window !== 'undefined' ? window.location.pathname : ''): void {
  capturedEmailSignInByPath.set(pathname, false);
}

const GUEST_RESTAURANT_SUBPATHS = [
  'menu',
  'browse',
  'cart',
  'reviews',
  'reserve-table',
  'join-queue',
  'join-order',
];

/**
 * Customer pages that stay in the app when a call returns 401.
 * The cart page opens the sign-in popup instead of sending the guest to /signin.
 */
export function isGuestAuthFlowPath(pathname: string): boolean {
  if (pathname === '/restaurants' || pathname === '/restaurants/') {
    return true;
  }

  const kioskMatch = pathname.match(/^\/kiosk\/[^/]+(?:\/(.*))?$/);
  if (kioskMatch) {
    const sub = (kioskMatch[1] || '').replace(/\/+$/, '');
    if (!sub || sub === 'login' || sub === 'menu' || sub.startsWith('menu/')) {
      return true;
    }
    return false;
  }

  const restaurantMatch = pathname.match(/^\/restaurants\/[^/]+(?:\/(.*))?$/);
  if (restaurantMatch) {
    const sub = (restaurantMatch[1] || '').replace(/\/+$/, '');
    if (!sub) {
      return true;
    }
    if (GUEST_RESTAURANT_SUBPATHS.some((prefix) => sub === prefix || sub.startsWith(`${prefix}/`))) {
      return true;
    }
    if (/^queue\//.test(sub) || /^orders\/\d+/.test(sub) || sub === 'my-orders' || sub.startsWith('my-orders/')) {
      return true;
    }
    return false;
  }

  return pathname.includes('/join-queue') || /\/queue\/[^/]+/.test(pathname);
}
